import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchSquishVinData } from 'site-modules/shared/utils/vin-decoder/fetch-squishvin-data';
import {
  transformOverviewData,
  transformPerformanceData,
  transformDimensionsData,
  transformSafetyData,
} from 'site-modules/shared/utils/vin-decoder/vehicle-data-formatters';
import { VinInfoSection } from 'site-modules/shared/components/vin-decoder/vin-info-section/vin-info-section';
import { StyledSelect } from 'site-modules/shared/components/styled-select/styled-select';
import { logger } from 'client/utils/logger/logger';

import './vehicle-info.scss';

const CUSTOM_FIELDS = `id,year,makeName,modelName,trim,price,transmissionType,engineType,engineCylinder,engineSize,numberOfSeats,categories,attributeGroups`;

function VehicleInfo({ vin = null, squishVin = null }) {
  const [vehicleData, setVehicleData] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!squishVin) {
      // Intentional reset when the VIN input is cleared.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsLoading(false);
      setVehicleData(null);
      setSelectedStyle(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    const loadVehicleData = async () => {
      try {
        const data = await fetchSquishVinData(squishVin, CUSTOM_FIELDS);
        if (data?.results?.length > 0) {
          setVehicleData(data.results);
          setSelectedStyle(data.results[0]);
        } else {
          setVehicleData([]);
          setSelectedStyle(null);
        }
      } catch (err) {
        setError('An error occurred while fetching data.');
        logger.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadVehicleData();
  }, [squishVin]);

  const handleTrimChange = useCallback(
    selectedOption => {
      const { styleId } = selectedOption || {};

      if (!styleId || styleId === 'undefined') {
        logger.warn('No valid styleId found in selected option:', selectedOption);
        return;
      }

      const selected = vehicleData.find(trimStyle => String(trimStyle.id) === String(styleId));

      if (selected) {
        setSelectedStyle(selected);
      } else {
        logger.warn('Could not find selected trim:', {
          selectedId: styleId,
          availableIds: vehicleData.map(t => t.id),
        });
      }
    },
    [vehicleData]
  );

  const trimOptions = vehicleData
    ? vehicleData.map(trimStyle => {
        const { id, trim } = trimStyle;
        return {
          styleId: String(id),
          name: trim?.name || 'Unknown Trim',
        };
      })
    : [];

  return (
    <div className="vehicle-info-container">
      <div className="vehicle-details-container">
        {isLoading && <div className="text-center p-8">Loading vehicle info...</div>}
        {error && <div className="text-center p-8 text-red-500">Error: {error}</div>}
        {!isLoading && !error && squishVin && vehicleData && vehicleData.length === 0 && (
          <div className="text-center p-8">No vehicle data found for this VIN.</div>
        )}

        {selectedStyle && (
          <>
            <h2 className="text-3xl font-bold text-gray-900">
              {selectedStyle.year} {selectedStyle.makeName} {selectedStyle.modelName}
            </h2>

            {vehicleData.length > 1 && (
              <>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg text-gray-700">
                  The data for this VIN includes specs for multiple trim levels. <br />
                  Please select one of the following trims to see its details.
                </div>
                <div className="mt-1 mb-2">
                  <StyledSelect
                    name="trim-select"
                    value={String(selectedStyle.id)}
                    onChange={handleTrimChange}
                    options={trimOptions}
                    valueKey="styleId"
                    labelKey="name"
                    wrapperClassName="d-inline-block"
                    overrideClasses="w-auto ps-0_5 pe-2_5 py-0_5"
                  />
                </div>
              </>
            )}

            <VinInfoSection title="Vehicle Overview" data={transformOverviewData(selectedStyle)} isFirstSection />
            <div className="px-1 px-md-1_5 mb-1 mb-md-1_5">
              <a
                href={`https://www.edmunds.com/appraisal/?vin=${vin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="appraisal-button"
              >
                See this car&apos;s value
              </a>
            </div>

            <VinInfoSection title="Performance & Economy" data={transformPerformanceData(selectedStyle)} />
            <VinInfoSection title="Dimensions & Capacity" data={transformDimensionsData(selectedStyle)} />
            <VinInfoSection title="IIHS Crash Test Ratings" data={transformSafetyData(selectedStyle)} />
          </>
        )}
      </div>
    </div>
  );
}

VehicleInfo.propTypes = {
  vin: PropTypes.string,
  squishVin: PropTypes.string,
};

export default VehicleInfo;
